import React, { useEffect, useState } from "react";
import DropdownHierarchyTwo from "../../../unicomponents/Dropdowns/DropdownHierarchyTwo";
import DropdownHierarchyThree from "../../../unicomponents/Dropdowns/DropdownHierarchyThree";
import DropdownUniqueUsersInDisplayData from "../../../unicomponents/Dropdowns/DropdownUniqueUsersInDisplayData";
import useEduActiStudents from "../../../hooks/useEduActiStudents";
import { useUIContext } from "../../../context/UIContext";
import { useQueryClient } from "@tanstack/react-query";
import {
  calculateTextNumber,
  calculateTextNumberByte,
} from "../../../util/etCetera";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

export default function ClubStudents({
  settingData,
  handleDropdownChange,
  schoolYear,
  hierarchyOneId,
  hierarchyTwoId,
  hierarchyThreeId,
  filters,
  selectedUser,
  selectedActivity,
  setStudentDataForDuplicateCheck,
  schoolStageId,
  eduActiStudentsData,
  isLoading,
  eduActiStudentsConditions,
  activeMenu,
}) {
  const { t } = useTranslation();

  const [savedRows, setSavedRows] = useState([]);
  const [savedBulk, setSavedBulk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState([]);
  const [studentRows, setStudentRows] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { setIsLoadingModal, handleToastCenterTop } = useUIContext();
  const { hasPermission } = useAuthContext();

  const { updateEduActiStudents, updateEduActiStudentsBulk, deleteEduActiStudents } = useEduActiStudents(
    eduActiStudentsConditions
  );

  // 저장 안 된 변경사항 경고
  useUnsavedChangesWarning(hasUnsavedChanges);

  const queryClient = useQueryClient();

  //여기서 학년, 반, 대상에 따라 filter되도록 하면 된다.
  useEffect(() => {
    // console.log("selectedActivity", selectedActivity);

    if (!eduActiStudentsData || !settingData) {
      return;
    }

    let updatedData = [];

    // eduActiStudentsData 배열을 수정된 객체로 매핑하여 새 배열 생성
    updatedData = eduActiStudentsData.map((studentData) => {
      // organization_path를 "/"로 분리하고 두 번째 요소(1번 인덱스)를 추출
      const pathParts = studentData.organization_path.split("/");
      // const stageId = pathParts[2]; //과정
      const yearId = pathParts[3]; //학년
      const classId = pathParts[4]; //반

      // console.log("pathParts", pathParts);
      // settingData에서 해당 stageId를 가진 객체의 group_name 찾기
      const setting = settingData.find(
        (setting) => setting.id === parseInt(studentData.hierarchy_one)
      );
      const groupName = setting ? setting.group_name : t("common.labels.whole"); // 해당하는 id가 없는 경우 "" 할당

      const student_year_row = settingData.find(
        (setting) => setting.id === parseInt(yearId)
      );

      const student_year = student_year_row
        ? student_year_row.group_name
        : t("common.labels.whole");

      const student_class_row = settingData.find(
        (setting) => setting.id === parseInt(classId)
      );
      const student_class = student_class_row
        ? student_class_row.group_name
        : t("common.labels.whole");

      // 원래 studentData 객체에 schoolStage 키 추가
      return {
        ...studentData,
        schoolStage: groupName,
        hierarchy_two: yearId,
        hierarchy_three: classId,
        student_year: student_year,
        student_class: student_class,
      };
    });

    setStudentDataForDuplicateCheck(updatedData);

    const filteredStudentList = updatedData.filter((row) => {
      //schoolStageId 필터링
      if (
        schoolStageId &&
        schoolStageId !== "0" &&
        row.hierarchy_one !== schoolStageId.toString()
      ) {
        return false;
      }

      // hierarchyTwoId 필터링
      if (
        hierarchyTwoId &&
        hierarchyTwoId !== "0" &&
        row.hierarchy_two !== hierarchyTwoId
      ) {
        return false;
      }

      // hierarchyThreeId 필터링
      if (
        hierarchyThreeId &&
        hierarchyThreeId !== "0" &&
        row.hierarchy_three !== hierarchyThreeId
      ) {
        return false;
      }

      if (
        selectedActivity?.id &&
        row.educational_activities_id !== selectedActivity?.id
      ) {
        return false;
      }

      // 모든 조건을 통과했다면, 이 요소를 포함시킨다.
      return true;
    });

    updatedData = updatedData.filter((row) => {
      //schoolStageId 필터링
      // console.log("schoolStageId", schoolStageId);
      if (
        schoolStageId &&
        schoolStageId !== "0" &&
        row.hierarchy_one !== schoolStageId.toString()
      ) {
        return false;
      }

      // hierarchyTwoId 필터링
      if (
        hierarchyTwoId &&
        hierarchyTwoId !== "0" &&
        row.hierarchy_two !== hierarchyTwoId
      ) {
        return false;
      }

      // hierarchyThreeId 필터링
      if (
        hierarchyThreeId &&
        hierarchyThreeId !== "0" &&
        row.hierarchy_three !== hierarchyThreeId
      ) {
        return false;
      }

      // username 필터링
      if (
        selectedUser &&
        selectedUser !== "" &&
        row.username !== selectedUser
      ) {
        return false;
      }

      // 모든 조건을 통과했다면, 이 요소를 포함시킨다.
      return true;
    });

    //선택된 활동이 없으면 유저가 선택되어야만 해당 유저의 데이터를 보여준다
    if (!selectedActivity?.id) {
      if (selectedUser && selectedUser !== "") {
        // 선택된 유저로 필터된 데이터만 보여줌
        const userFilteredData = updatedData.filter(
          (row) => row.username === selectedUser
        );
        setStudentRows(userFilteredData);
        setStudentList(filteredStudentList);
      } else {
        // 유저가 선택되지 않았으면 아무것도 보여주지 않음
        setStudentRows([]);
        setStudentList(filteredStudentList);
      }
      return;
    }

    // Check if selectedActivity is valid and if rows match selectedActivity.id
    const filteredByActivity = updatedData.filter(
      (row) => row.educational_activities_id === selectedActivity?.id
    );

    // 활동이 선택되었지만 해당하는 학생이 없으면 빈 화면
    if (filteredByActivity.length === 0) {
      setStudentRows([]);
      setStudentList([]);
      return;
    }

    setStudentRows(filteredByActivity);
    setStudentList(filteredStudentList);
  }, [
    eduActiStudentsData,
    hierarchyThreeId,
    hierarchyTwoId,
    schoolStageId,
    selectedActivity,
    selectedUser,
    setStudentDataForDuplicateCheck,
    settingData,
    t,
  ]);

  if (isLoading) return <div>{t("common.messages.selectActivity")}</div>;

  // 일반적인 row 데이터 변경을 처리하는 함수
  const handleTrChange = (rowIndex, event) => {
    // const { value } = event.target;
    const field = event.target.name;

    const value = field.includes("extra_attributes")
      ? {
          ...studentRows[rowIndex].extra_attributes,
          [event.target.id]: event.target.value,
        }
      : event.target.value;
    // console.log("value", value);
    const updatedRows = studentRows.map((row, index) =>
      index === rowIndex ? { ...row, [field]: value, isModified: true } : row
    );
    // console.log("updatedRows tr", updatedRows);
    setStudentRows(updatedRows);
    setHasUnsavedChanges(true);
  };

  // 태그 변경을 처리하는 함수
  const handleTagChange = (tagName, event, rowIndex, tagIndex) => {
    const { value } = event.target; // 선택된 새로운 태그 값

    // 먼저 해당하는 row를 찾아서 복사한 다음
    const updatedRows = studentRows.map((row, index) => {
      if (index === rowIndex) {
        const updatedTags = row.tags.map((tag, idx) => {
          if (idx === tagIndex) {
            return { [tagName]: value }; // 태그 값을 업데이트
          }
          return tag;
        });

        return { ...row, tags: updatedTags, isModified: true };
      }
      return row;
    });

    // console.log("updatedRows", updatedRows);
    setStudentRows(updatedRows); // 상태 업데이트
    setHasUnsavedChanges(true);
  };

  const handleSubmit = (index) => {
    const dataSet = studentRows[index];
    if (!dataSet.isModified) {
      // 변경사항이 없는 경우, 제출을 건너뜁니다.
      console.log("건너뛰기");
      return;
    }

    if (
      !hasPermission(
        dataSet?.educational_activity?.creator_username,
        "can_update"
      )
    ) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }
    // 변경된 데이터 세트 처리 로직...
    // console.log("제출된 데이터 세트:", dataSet);
    setIsLoadingModal(true);
    updateEduActiStudents.mutate(dataSet, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedRows((prev) => [...prev, dataSet.id]);
        setTimeout(() => {
          setSavedRows([]);
        }, 1000);
        // 저장 후 남은 변경사항 체크
        const hasRemainingChanges = studentRows.some(
          (d, i) => i !== index && d.isModified
        );
        if (!hasRemainingChanges) {
          setHasUnsavedChanges(false);
        }
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
    // 성공적으로 제출 후, 해당 데이터 세트의 isModified 상태를 초기화합니다.
    setStudentRows((currentDataSets) =>
      currentDataSets.map((dataSet, dataSetIndex) =>
        dataSetIndex === index ? { ...dataSet, isModified: false } : dataSet
      )
    );
  };

  // 모두 저장 함수
  const handleSubmitAll = () => {
    const dataArray = studentRows.filter((data) => data.isModified === true);

    if (dataArray.length < 1) {
      console.log("건너뛰기");
      handleToastCenterTop(t("common.messages.notModifed"));
      return;
    }

    // 권한 체크 - 모든 수정 대상에 대해 권한 확인
    const hasNoPermission = dataArray.some(
      (data) =>
        !hasPermission(
          data?.educational_activity?.creator_username,
          "can_update"
        )
    );

    if (hasNoPermission) {
      handleToastCenterTop(t("common.messages.ownerUpdatePermission"));
      return;
    }

    setIsLoadingModal(true);
    updateEduActiStudentsBulk.mutate(dataArray, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setSavedBulk(true);
        setTimeout(() => {
          setSavedBulk(false);
        }, 1000);

        setStudentRows((currentDataSets) =>
          currentDataSets.map((data) =>
            data.isModified === true ? { ...data, isModified: false } : data
          )
        );
        setHasUnsavedChanges(false);
      },
      onError: (error) => {
        setIsLoadingModal(false);
        console.error(error);
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  const handleConfirmDelete = (row) => {
    setConfirmDelete((prev) => [...prev, row.id]);
  };

  const handleCancelDelete = () => {
    setConfirmDelete([]);
  };

  const handleDelete = (row) => {
    const id = row.id;

    if (
      !hasPermission(row?.educational_activity?.creator_username, "can_delete")
    ) {
      handleToastCenterTop(t("common.messages.ownerDeletePermission"));
      return;
    }

    setIsLoadingModal(true);
    deleteEduActiStudents.mutate(id, {
      onSuccess: (successMessage) => {
        console.log(successMessage);
        setIsLoadingModal(false);
        setConfirmDelete([]);
        queryClient.invalidateQueries(["eduActiStudents"]);
        setStudentRows((rows) => rows.filter((row) => row.id !== id));
      },
      onError: (error) => {
        setIsLoadingModal(false);
        setConfirmDelete([]);
        console.error(error); // 일단 콘솔에 에러를 출력합니다.
        console.error("위와 같은 에러가 났으니 고치삼");
        alert(t("common.messages.errorAlert") + error.message);
      },
    });
  };

  return (
    <>
      <div className="row">
        <div className="col-3">
          <h4>{t("common.labels.assign")}</h4>
        </div>
        <div className="col-7 d-flex mb-3">
          <DropdownHierarchyTwo
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={schoolYear}
            hierarchyOneId={hierarchyOneId}
            hierarchyTwoId={hierarchyTwoId}
            parentId={false}
          />

          <DropdownHierarchyThree
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            schoolYear={schoolYear}
            hierarchyTwoId={hierarchyTwoId}
            hierarchyThreeId={hierarchyThreeId}
          />

          <DropdownUniqueUsersInDisplayData
            displayData={studentList}
            displayKey={"fullname"}
            valueKey={"username"}
            filters={filters}
            uniqueKey={"username"}
            sortKey={"fullname"}
            isDesc={false}
            handleDropdownChange={handleDropdownChange}
            selectedUser={selectedUser}
            selectName={"students"}
          />
        </div>
        <div className="col-2 text-end">
          {studentRows.length > 0 && (
            <button
              type="button"
              onClick={handleSubmitAll}
              className="btn btn-sm btn-primary"
            >
              {savedBulk
                ? t("common.labels.savedWell") || "저장완료"
                : t("common.labels.saveAll") || "모두저장"}
            </button>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="container-fluid">
        <table
          className="table table-bordered"
          // style={{ border: "1px solid #000", borderCollapse: "collapse" }}
        >
          <thead>
            <tr className="table-secondary">
              <th width={50}>{t("common.labels.schoolStage")}</th>
              <th width={50}>{t("common.labels.studentYear")}</th>
              <th width={50}>{t("common.labels.class")}</th>
              <th width={70}>{t("common.user.fullname") || "이름"}</th>
              <th width={150}>{t("common.labels.activityName")}</th>
              <th width={50}>{t("common.labels.hours") || "시간"}</th>
              <th width={90}>{t("common.labels.shortStudentLifeRecord")}</th>
              <th style={{ minWidth: "150px" }}>
                {t("common.labels.specialNote")}
              </th>
              <th
                className="p-0 text-center align-middle"
                width={50}
                style={{ fontSize: "13px" }}
              >
                {t("common.labels.textLength")}
              </th>
              <th width={60}></th>
            </tr>
          </thead>
          <tbody>
            {studentRows?.map((row, index) => (
              <Trs
                key={row.id}
                row={row}
                index={index}
                handleTrChange={handleTrChange}
                handleSubmit={handleSubmit}
                handleTagChange={handleTagChange}
                savedRows={savedRows}
                handleDelete={handleDelete}
                confirmDelete={confirmDelete}
                handleConfirmDelete={handleConfirmDelete}
                handleCancelDelete={handleCancelDelete}
              />
            ))}
          </tbody>
        </table>
      </form>
    </>
  );
}

function Trs({
  row,
  index,
  handleTrChange,
  handleSubmit,
  handleTagChange,
  savedRows,
  handleDelete,
  confirmDelete,
  handleConfirmDelete,
  handleCancelDelete,
}) {
  const { t } = useTranslation();

  return (
    <React.Fragment>
      <tr className="align-middle text-center" style={{ fontSize: "14px" }}>
        <td className="pb-0 pt-0" style={{ fontSize: "13px" }}>
          {row?.schoolStage || ""}
        </td>
        <td className="pb-0 pt-0" style={{ fontSize: "13px" }}>
          {row?.student_year || ""}
        </td>
        <td className="pb-0 pt-0" style={{ fontSize: "13px" }}>
          {row?.student_class || ""}
        </td>
        <td className="pb-0 pt-0" style={{ fontSize: "13px" }}>
          {row.fullname}
        </td>
        <td className="pb-0 pt-0">
          <textarea
            className="input-like-span"
            rows={3}
            name="title"
            value={row?.title || ""}
            onChange={(e) => handleTrChange(index, e)}
          />
        </td>
        <td className="pb-0 pt-0 align-middle ps-1 pe-1">
          <input
            type="number"
            className="input-like-span"
            name="extra_attributes"
            id="hours"
            value={row?.extra_attributes?.hours || ""}
            onChange={(e) => handleTrChange(index, e)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit(index); // Enter 키를 눌렀을 때 handleSubmit 호출
              }
            }}
          />
        </td>
        <td className="pb-0 pt-0">
          <select
            className="select-like-span"
            name="on_schoollife_record"
            value={row?.on_schoollife_record || "등재"} // 각 행의 선택된 태그 값을 반영
            onChange={(e) => handleTrChange(index, e)}
          >
            <option value="등재">{t("common.status.onRecord")}</option>
            <option value="미등재">{t("common.status.notOnRecord")}</option>
          </select>
        </td>
        <td className="pb-0 pt-0">
          <textarea
            type="text"
            className="input-like-span"
            rows={3}
            name="content"
            value={row.content}
            onChange={(e) => handleTrChange(index, e)}
          />
        </td>
        <td className="p-0 text-center" style={{ fontSize: "12px" }}>
          {calculateTextNumber(row?.content || "")} /
          {calculateTextNumberByte(row?.content || "")}
        </td>
        <td rowSpan={2} valign="middle" className="pb-0 pt-0">
          <button
            type="button"
            onClick={() => handleSubmit(index)}
            className="btn btn-sm p-1 btn-primary m-0"
          >
            {savedRows.includes(row.id)
              ? t("common.labels.savedWell")
              : t("common.labels.save") || "저장"}
          </button>
          {confirmDelete.includes(row.id) ? (
            <>
              <button
                type="button"
                onClick={() => handleDelete(row)}
                className="btn btn-sm p-1 btn-danger m-0 mt-1"
              >
                {t("common.labels.confirm") || "확인"}
              </button>
              <button
                type="button"
                onClick={() => handleCancelDelete()}
                className="btn btn-sm p-1 btn-secondary m-0 mt-1"
              >
                {t("common.labels.cancel") || "취소"}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => handleConfirmDelete(row)}
              className="btn btn-sm p-1 btn-warning m-0 mt-1"
            >
              {t("common.labels.delete") || "삭제"}
            </button>
          )}
        </td>
      </tr>
      <tr style={{ borderBottom: "3px solid #000" }}>
        <th className="pb-0 pt-0 align-middle">{t("common.labels.tag")}</th>
        <td colSpan={8} className="pb-1 pt-1">
          <div className="d-flex" style={{ fontSize: "14px" }}>
            <div
              style={{ display: "flex", alignItems: "flex-end", gap: "5px" }}
            >
              {row?.tags?.map((tag, tagIndex) => {
                const tagName = Object.keys(tag)[0];
                return (
                  <div
                    key={tagIndex}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <div
                      className="text-center"
                      style={{ marginBottom: "5px" }}
                    >
                      {tagName}
                    </div>
                    <select
                      className="form-control p-1"
                      value={tag[tagName]}
                      name="tags"
                      onChange={(e) =>
                        handleTagChange(tagName, e, index, tagIndex)
                      }
                    >
                      <option value="1">{t("common.labels.veryWeak")}</option>
                      <option value="2">{t("common.labels.weak")}</option>
                      <option value="3">{t("common.labels.normal")}</option>
                      <option value="4">{t("common.labels.strong")}</option>
                      <option value="5">{t("common.labels.veryStrong")}</option>
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </td>
      </tr>
    </React.Fragment>
  );
}
